export type Role = 'owner' | 'manager' | 'cashier';

/** What each role is called on screen. The codes stay as they are in the API. */
export const ROLE_LABEL: Record<Role, string> = { owner: 'Admin', manager: 'Manager', cashier: 'Captain' };
export type Features = Record<string, boolean>;

export interface Barcode { id: number; code: string; format: string; is_generated: boolean }

export interface Product {
  id: number; name: string; brand: string | null; category_id: number | null; category: string | null;
  unit: string; size: string | null; size_ml: number | null; pack_size: number | null; pack_unit: string | null;
  mrp_paise: number; price_paise: number; cost_paise?: number; gst_percent: number; hsn: string | null;
  reorder_level: number; lead_time_days: number | null; voice_aliases: string[]; plu_code: number | null;
  stock: number; stock_milli: number; is_active: boolean; barcodes: Barcode[];
  last_sold_at: string | null; last_received_at: string | null;
}

export interface Category { id: number; name: string; sort_order: number; is_active: boolean; product_count?: number }

export interface ShopInfo {
  id: number; code: string; name: string; shop_type: string; address: string | null; phone: string | null;
  email: string | null; state: string | null; gstin: string | null; license_no: string | null; status: string;
  settings: Record<string, any>; features: Features; owner_editable: string[]; plan_name: string;
  max_users: number; max_counters: number; suspended_for_dues: boolean;
}

export interface UserInfo {
  id: number | null; username: string; full_name: string; role: Role; phone?: string | null;
  is_active?: boolean; must_change_password?: boolean; assigned_counter_id?: number | null; last_login_at?: string | null;
}

export interface CounterInfo { id: number; name: string; kind: string; printer: Record<string, any> }

export interface SubscriptionState {
  state: 'none' | 'ok' | 'reminder' | 'grace' | 'expired';
  billing_allowed: boolean;
  amount_due_paise?: number; due_date?: string | null; pay_by?: string | null; days_until_due?: number | null;
}

export interface Session {
  user: UserInfo; shop: ShopInfo; counter: CounterInfo | null; read_only?: boolean;
  must_change_password?: boolean; subscription?: SubscriptionState;
}

export interface BillItem {
  id: number; product_id: number; name: string; brand: string | null; size: string | null; unit: string | null;
  barcode: string | null; hsn: string | null; qty: number; mrp_paise: number; unit_price_paise: number;
  gross_paise: number; line_discount_paise: number; bill_discount_share_paise: number; net_paise: number;
  gst_percent: number; tax_paise: number; returned: number;
}

export interface Payment { mode: string; amount_paise: number; tendered_paise: number | null; reference: string | null }

export interface Bill {
  id: number; bill_no: number; bill_number: string; status: string; counter_name: string | null;
  cashier_name: string | null; customer_name: string | null; customer_phone: string | null; customer_gstin: string | null;
  gross_paise: number; line_discount_paise: number; bill_discount_paise: number; total_paise: number;
  tax_paise: number; cgst_paise: number; sgst_paise: number; paid_paise: number; change_paise: number;
  item_count: number; business_date: string; created_at: string; voided_at: string | null;
  voided_by: string | null; void_reason: string | null; items: BillItem[]; payments: Payment[]; replayed?: boolean;
}

export interface TaxRow { gst_percent: number; hsn: string | null; taxable_paise: number; tax_paise: number; cgst_paise: number; sgst_paise: number }

export interface ReceiptData {
  bill: Bill;
  shop: { name: string; address: string | null; phone: string | null; gstin: string | null; license_no: string | null;
          shop_type: string; settings: Record<string, any> };
  gst_invoice: boolean;
  tax_summary: TaxRow[];
}

export interface LabelFields { [k: string]: boolean }
export interface LabelTemplate {
  id: string; name: string; kind: 'roll' | 'sheet' | 'tag'; label_width_mm: number; label_height_mm: number;
  columns: number; rows: number; page_width_mm: number; page_height_mm: number; gap_x_mm: number; gap_y_mm: number;
  margin_top_mm: number; margin_left_mm: number; fields: LabelFields; builtin: boolean; db_id?: number;
  font_scale?: number; barcode_height_mm?: number;
}

export interface LabelItem {
  product_id: number; name: string; brand: string | null; size: string | null; mrp_paise: number; price_paise: number;
  qty: number; barcode: string | null; barcode_format: string | null; shop_name: string;
  batch?: string; mfg_date?: string; expiry_date?: string; packed_on?: string; net_weight?: string;
}
